import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
  TextField,
  Switch,
  Popover,
  Checkbox,
  FormControlLabel,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import './CreateGroupSessonModel.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
// import { format, parse, set } from 'date-fns';
import ScheduleCreationSuccessModel from '../ScheduleCreationSuccessModel/ScheduleCreationSuccessModel';
import DropDownIcon from '../../../../assets/ScheduleIcon/dropdown.svg';
import { toast } from 'react-hot-toast';
// Import time picker components
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReactDOM from 'react-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Setup dayjs plugins for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom renderer that hides the minute hand in the hours view
const customTimeViewRenderer = props => {
  const { view, value, onChange, ampm, ...other } = props;

  // For hours view, render the standard clock
  if (view === 'hours') {
    return renderTimeViewClock({
      view,
      value,
      onChange,
      ampm,
      ...other,
    });
  }

  // For minutes view, which is shown after hours are selected
  return renderTimeViewClock({
    view,
    value,
    onChange,
    ampm,
    ...other,
  });
};

// Revise the LevelMap to be consistent with display values as keys and API values as values
const LevelMap = {
  // Academic levels
  Elementary: 'elementary',
  'High School': 'high_school',
  'Middle School': 'middle_school',
  University: 'university',
  // Creative levels
  Beginner: 'beginner',
  Intermediate: 'intermediate',
  Professional: 'professional',
};

// Create the reverse map for displaying values based on API values
const ReverseLevelMap = Object.entries(LevelMap).reduce((acc, [display, api]) => {
  acc[api] = display;
  return acc;
}, {});

const CreateGroupSessionModel = ({ open, onClose, onConfirm }) => {
  const [subject, setSubject] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [level, setLevel] = useState('');
  const [days, setDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  const [timeSlots, setTimeSlots] = useState({
    monday: [{ from: '', to: '' }],
    tuesday: [{ from: '', to: '' }],
    wednesday: [{ from: '', to: '' }],
    thursday: [{ from: '', to: '' }],
    friday: [{ from: '', to: '' }],
    saturday: [{ from: '', to: '' }],
    sunday: [{ from: '', to: '' }],
  });

  // State for currently active time picker to track which one is open
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [clockView, setClockView] = useState('hours'); // 'hours' or 'minutes'

  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [inputTag, setInputTag] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const [copySourceDay, setCopySourceDay] = useState('');
  const [copySourceIndex, setCopySourceIndex] = useState(null);
  const [copyTargetDays, setCopyTargetDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });
  const [existingAvailability, setExistingAvailability] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [teacherData, setTeacherData] = useState(null);
  const [teacherType, setTeacherType] = useState('academic');

  // Add refs for time picker positioning
  const timePickerRefs = useRef({});

  // Fetch existing availability and teacher data when modal opens
  useEffect(() => {
    if (open) {
      fetchTeacherData();
    }
  }, [open]);

  // Update available tags when subject changes
  useEffect(() => {
    if (subjectId && teacherData) {
      // Find the selected subject in teacher data
      const selectedSubject = teacherData.subjects.find(s => s.id === subjectId);
      if (selectedSubject && selectedSubject.tags) {
        setAvailableTags(selectedSubject.tags);
      } else {
        setAvailableTags([]);
      }
    } else {
      setAvailableTags([]);
      setTags([]);
    }
  }, [subjectId, teacherData]);

  const fetchTeacherData = async () => {
    try {
      const response = await axiosInstance.get('/teachers');
      console.log('API response teacher data:', response.data);
      setTeacherData(response.data);
      const teacherTypeFromApi = response.data.teacher_type || 'academic';
      setTeacherType(teacherTypeFromApi);

      // Set subjects from teacher data
      if (response.data.subjects && Array.isArray(response.data.subjects)) {
        setSubjects(response.data.subjects);
      }

      // We don't preselect any level now, as requested
      // Just log available levels for debugging
      if (
        response.data.level_of_teaching &&
        Array.isArray(response.data.level_of_teaching) &&
        response.data.level_of_teaching.length > 0
      ) {
        console.log('Available teaching levels:', response.data.level_of_teaching);
      }
    } catch (error) {
      console.error('Error fetching teacher data:', error);
    }
  };

  // Utility function to convert time string to minutes
  const convertToMinutes = timeString => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const fetchExistingAvailability = async () => {
    try {
      const response = await axiosInstance.get('/teachers/availability?session_type=group');

      // Support both old array and new paginated shape { count, results: [] }
      const payload = response.data?.data || response.data;
      const items = Array.isArray(payload) ? payload : payload?.results || [];

      // Convert times from UTC to local timezone
      const localizedAvailability = items.map(item => {
        const availability = (item.availability || []).map(slot => {
          // Convert both startTime and endTime from UTC to local time format
          const localStartTime = convertUTCToLocal(slot.startTime);
          const localEndTime = convertUTCToLocal(slot.endTime);

          // Handle overnight shifts - if endTime in UTC is less than startTime, it might be an overnight shift
          const startMinutes = convertToMinutes(slot.startTime);
          const endMinutes = convertToMinutes(slot.endTime);
          const isOvernightShift = endMinutes < startMinutes;

          return {
            ...slot,
            startTime: localStartTime,
            endTime: localEndTime,
            isOvernightShift,
          };
        });

        return {
          ...item,
          availability,
        };
      });

      setExistingAvailability(localizedAvailability);
    } catch (error) {
      console.error('Error fetching existing availability:', error);
    }
  };

  const handleDayToggle = day => {
    setDays({
      ...days,
      [day]: !days[day],
    });

    // Clear error when toggling days
    setErrorMessage('');
  };

  // Updated function to handle opening a time picker
  const handleTimePickerOpen = (day, index, field) => {
    // Reset the current field's value when opening the picker to allow re-selection
    if (timeSlots[day][index][field]) {
      const newTimeSlots = { ...timeSlots };
      // Don't actually clear the value in the UI, just in the internal state when opening
      // This allows the picker to reset while keeping the display value
      setActiveTimePicker({
        day,
        index,
        field,
        previousValue: newTimeSlots[day][index][field], // Store previous value
      });
    } else {
      setActiveTimePicker({ day, index, field });
    }
    setClockView('hours'); // Always start with hours view
  };

  // Function to handle closing a time picker
  const handleTimePickerClose = () => {
    // If user cancels without selecting a new value, restore the previous value
    if (activeTimePicker?.previousValue) {
      const { day, index, field, previousValue } = activeTimePicker;
      const newTimeSlots = { ...timeSlots };

      // Only restore if the value was actually cleared (user didn't select a new time)
      if (!newTimeSlots[day][index][field]) {
        newTimeSlots[day][index][field] = previousValue;
        setTimeSlots(newTimeSlots);
      }
    }

    setActiveTimePicker(null);
    setClockView('hours'); // Reset to hours view
  };

  // Modified handleTimeChange to handle sequential time selection
  const handleTimeChange = (day, index, field, value) => {
    // If value is null, the field is being cleared
    if (!value) {
      const newTimeSlots = { ...timeSlots };
      newTimeSlots[day][index][field] = '';
      setTimeSlots(newTimeSlots);
      setClockView('hours'); // Reset to hours view
      return;
    }

    const newTimeSlots = { ...timeSlots };

    // For hours selection, update the hours and keep existing minutes or use 00
    if (clockView === 'hours') {
      const currentTimeValue = newTimeSlots[day][index][field];

      if (currentTimeValue) {
        // If there's an existing time value, extract its minutes
        const existingTime = stringToTime(currentTimeValue);
        if (existingTime) {
          // Keep the existing minutes but update the hours
          const updatedTime = value.minute(existingTime.minute());
          newTimeSlots[day][index][field] = updatedTime.format('hh:mm A');
          console.log(`Updated hours only: ${updatedTime.format('hh:mm A')}`);
        } else {
          // If parsing failed, use 00 minutes with the new hour
          newTimeSlots[day][index][field] = value.minute(0).format('hh:mm A');
          console.log(`Reset to whole hour: ${value.minute(0).format('hh:mm A')}`);
        }
      } else {
        // If no existing time, use 00 minutes with the new hour
        newTimeSlots[day][index][field] = value.minute(0).format('hh:mm A');
        console.log(`New whole hour: ${value.minute(0).format('hh:mm A')}`);
      }

      setTimeSlots(newTimeSlots);
      // Switch to minutes view to let user select minutes if desired
      setClockView('minutes');
      return;
    }

    // For minutes selection, round to nearest 15 minutes and format the time
    const minutes = value.minute();
    const roundedMinutes = Math.round(minutes / 15) * 15;

    // Create a new dayjs object with rounded minutes
    const roundedTime = value.minute(roundedMinutes === 60 ? 0 : roundedMinutes);
    // If rounding to 60, increment the hour
    const adjustedTime = roundedMinutes === 60 ? roundedTime.add(1, 'hour') : roundedTime;

    // Format in 12-hour format with AM/PM for display
    const formattedTime = adjustedTime.format('hh:mm A');
    newTimeSlots[day][index][field] = formattedTime;

    console.log(
      `Final time selection: ${formattedTime} (rounded from ${minutes} to ${roundedMinutes} minutes)`
    );

    // We're removing the validation that requires end time to be after start time
    // Any case where to < from will be treated as an overnight session

    setTimeSlots(newTimeSlots);

    // Reset to hours view for next selection and close the picker
    setClockView('hours');
    setActiveTimePicker(null);
  };

  // Utility function to convert string time to dayjs object
  const stringToTime = timeString => {
    if (!timeString) return null;
    // Parse time including AM/PM format
    return dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
  };

  // Generate time options with 15-minute intervals (keep this for backward compatibility)
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const formattedHour = hour.toString().padStart(2, '0');

      // Only add minutes at 00, 15, 30, 45
      for (let minute of [0, 15, 30, 45]) {
        const formattedMinute = minute.toString().padStart(2, '0');
        const time = `${formattedHour}:${formattedMinute}`;
        options.push(time);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Check if a time is already scheduled for a specific day
  const isTimeScheduled = (day, time) => {
    // Convert day format to match API response (capitalize first letter)
    const formattedDay = day.charAt(0).toUpperCase() + day.slice(1);

    // Check against all existing availability slots
    for (const availability of existingAvailability) {
      for (const slot of availability.availability || []) {
        if (slot.days.includes(formattedDay)) {
          // Check if time falls within this slot
          if (time >= slot.startTime && time < slot.endTime) {
            return true;
          }
        }
      }
    }
    return false;
  };

  // Get available time options for a specific day and field (from/to)
  const getAvailableTimeOptions = (day, field, currentValue) => {
    // If this is a "to" field and we have a "from" value, filter out times before "from"
    if (field === 'to' && currentValue.from) {
      return timeOptions.filter(time => time > currentValue.from && !isTimeScheduled(day, time));
    }

    // For "from" field or if no "from" value, just filter out scheduled times
    return timeOptions.filter(time => !isTimeScheduled(day, time));
  };

  const addTimeSlot = day => {
    const newTimeSlots = { ...timeSlots };
    newTimeSlots[day].push({ from: '', to: '' });
    setTimeSlots(newTimeSlots);
  };

  const removeTimeSlot = (day, index) => {
    const newTimeSlots = { ...timeSlots };
    newTimeSlots[day].splice(index, 1);
    if (newTimeSlots[day].length === 0) {
      newTimeSlots[day].push({ from: '', to: '' });
    }
    setTimeSlots(newTimeSlots);
  };

  const handleTagChange = (event, newTags) => {
    console.log('Selected tags:', newTags);
    // Make sure all tags have proper structure
    const validTags = newTags.filter(tag => tag && (tag.title || typeof tag === 'string'));
    setTags(validTags);
    // Clear error when changing tags
    setErrorMessage('');
  };

  const handleTagDelete = tagToDelete => {
    const newTags = tags.filter(tag => {
      // Compare by id if available, otherwise by title
      if (tag.id && tagToDelete.id) {
        return tag.id !== tagToDelete.id;
      }
      // Compare by title as fallback
      const tagTitle = typeof tag === 'string' ? tag : tag?.title || '';
      const deleteTitle = typeof tagToDelete === 'string' ? tagToDelete : tagToDelete?.title || '';
      return tagTitle !== deleteTitle;
    });
    setTags(newTags);
  };

  const handleTagInputChange = (event, newInputTag) => {
    setInputTag(newInputTag);
  };

  const handleSubjectChange = e => {
    const selectedSubject = e.target.value;
    setSubject(selectedSubject);

    // Find the subject ID
    const subjectData = subjects.find(s => s.title === selectedSubject);
    console.log('Selected subject data:', subjectData);
    if (subjectData && subjectData.id) {
      setSubjectId(subjectData.id);
      // Clear tags when subject changes
      setTags([]);
    } else {
      setSubjectId('');
    }

    // Clear error when changing subject
    setErrorMessage('');
  };

  const openCopyPopover = (event, day, index) => {
    setCopyAnchorEl(event.currentTarget);
    setCopySourceDay(day);
    setCopySourceIndex(index);

    // Reset target days except the source day
    const newCopyTargetDays = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };
    setCopyTargetDays(newCopyTargetDays);

    // Clear any previous errors related to copying
    setErrorMessage('');
  };

  const closeCopyPopover = () => {
    setCopyAnchorEl(null);
    setCopySourceDay('');
    setCopySourceIndex(null);
  };

  const handleCopyTargetChange = day => {
    setCopyTargetDays({
      ...copyTargetDays,
      [day]: !copyTargetDays[day],
    });
  };

  const applyCopy = () => {
    const sourceSlot = timeSlots[copySourceDay][copySourceIndex];

    // Prevent copying if source slot is incomplete
    if (!sourceSlot?.from || !sourceSlot?.to) {
      setErrorMessage(
        'Please select both "From" and "To" times for the source slot before copying.'
      );
      return;
    }

    // Determine selected target days excluding the source day
    const selectedTargets = Object.keys(copyTargetDays).filter(
      day => day !== copySourceDay && copyTargetDays[day]
    );

    if (selectedTargets.length === 0) {
      setErrorMessage('Please select at least one day to copy to.');
      return;
    }

    const newTimeSlots = { ...timeSlots };
    const newDays = { ...days };

    selectedTargets.forEach(day => {
      // Enable the day if it's not already enabled
      newDays[day] = true;

      // Ensure the day has a time slots array
      if (!Array.isArray(newTimeSlots[day])) {
        newTimeSlots[day] = [];
      }

      // If there are no slots yet, create the first one
      if (newTimeSlots[day].length === 0) {
        newTimeSlots[day] = [{ from: sourceSlot.from, to: sourceSlot.to }];
      } else {
        // If there is a single empty default slot, fill it instead of adding a new one
        const hasOnlyEmptyDefault =
          newTimeSlots[day].length === 1 &&
          !newTimeSlots[day][0]?.from &&
          !newTimeSlots[day][0]?.to;

        if (hasOnlyEmptyDefault) {
          newTimeSlots[day][0] = { from: sourceSlot.from, to: sourceSlot.to };
        } else {
          // Avoid adding duplicate slots
          const isDuplicate = newTimeSlots[day].some(
            s => s.from === sourceSlot.from && s.to === sourceSlot.to
          );

          if (!isDuplicate) {
            newTimeSlots[day].push({ from: sourceSlot.from, to: sourceSlot.to });
          }
        }
      }
    });

    setDays(newDays);
    setTimeSlots(newTimeSlots);

    // Clear errors and close popover after successful copy
    setErrorMessage('');
    closeCopyPopover();
  };

  const validateForm = () => {
    // Check if subject is selected
    if (!subject) {
      setErrorMessage('Please select a subject');
      return false;
    }

    // Check if level is selected
    if (!level) {
      setErrorMessage('Please select a level to teach');
      return false;
    }

    // Check if at least one day is selected
    const anyDaySelected = Object.values(days).some(value => value);
    if (!anyDaySelected) {
      setErrorMessage('Please select at least one day');
      return false;
    }

    // Check if all selected days have valid time slots
    let isValid = true;
    Object.keys(days).forEach(day => {
      if (days[day]) {
        timeSlots[day].forEach(slot => {
          if (!slot.from || !slot.to) {
            setErrorMessage('Please fill in all time slots');
            isValid = false;
          }
        });
      }
    });

    if (!isValid) return false;

    return true;
  };

  const handleConfirm = async () => {
    if (validateForm()) {
      setIsLoading(true);

      try {
        // Format data for API - convert to 24h format in UTC
        const timeSlotsByDay = {};

        Object.keys(days).forEach(day => {
          if (days[day]) {
            const capitalizedDay = day.charAt(0).toUpperCase() + day.slice(1);
            timeSlotsByDay[capitalizedDay] = timeSlots[day].map(slot => {
              // First, parse the local times using dayjs
              const fromTime = dayjs(`2023-01-01 ${slot.from}`, 'YYYY-MM-DD hh:mm A');
              const toTime = dayjs(`2023-01-01 ${slot.to}`, 'YYYY-MM-DD hh:mm A');

              // Check if this is an overnight session (to < from in local time)
              const isOvernightShift = toTime.isBefore(fromTime);

              // Get the current timezone offset for debugging
              const tzOffset = new Date().getTimezoneOffset();
              console.log(`Current timezone offset: ${tzOffset} minutes`);

              // Convert each time to UTC in 24-hour format (HH:MM)
              let startUTC = fromTime.utc().format('HH:mm');
              let endUTC;

              // For overnight shifts, add a day to the end time before converting to UTC
              if (isOvernightShift) {
                const toTimeNextDay = toTime.add(1, 'day');
                endUTC = toTimeNextDay.utc().format('HH:mm');
                console.log(
                  `Overnight shift: ${slot.from} -> ${startUTC}, ${slot.to} (next day) -> ${endUTC}`
                );
              } else {
                endUTC = toTime.utc().format('HH:mm');
                console.log(`Regular shift: ${slot.from} -> ${startUTC}, ${slot.to} -> ${endUTC}`);
              }

              // Ensure proper 24-hour format without any extra characters
              startUTC = startUTC.trim();
              endUTC = endUTC.trim();

              return {
                start: startUTC,
                end: endUTC,
              };
            });
          }
        });

        // Get the API level value from the LevelMap
        const levelFormatted = LevelMap[level] || '';

        // Use tag IDs instead of titles for the API
        const tagIds = tags.map(tag => tag?.id || '').filter(id => id !== '');

        const requestData = {
          session_type: 'group',
          description: description,
          levels_to_teach: levelFormatted, // Single value, not an array
          subject: subjectId, // Use the subjectId instead of lowercase title
          tags: tagIds, // Send an array of tag IDs
          time_slots: timeSlotsByDay,
        };

        console.log('Sending API request with data:', requestData);

        // Make API call
        const response = await axiosInstance.post('/teachers/availability', requestData);
        console.log('Successfully created availability:', response.data);

        // Show success message
        setShowSuccess(true);
        console.log('Setting showSuccess to true');
        toast.success('Group session created successfully!');

        // Call the onConfirm prop with the form data (for backward compatibility)
        if (onConfirm) {
          // Filter only the selected days with their time slots
          const selectedDaysWithTimeSlots = {};
          Object.keys(days).forEach(day => {
            if (days[day]) {
              selectedDaysWithTimeSlots[day] = timeSlots[day];
            }
          });

          onConfirm({
            subject,
            level,
            tags: tagIds,
            days: selectedDaysWithTimeSlots,
            description,
          });
        }

        // Refresh calendar data
        if (window.refreshCalendarData) {
          window.refreshCalendarData();
        }

        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          resetForm();
          setShowSuccess(false);
          onClose();
        }, 2000);
      } catch (error) {
        console.error('Error creating availability:', error);

        // Format error message from API correctly
        let errorMsg = 'Failed to create availability. Please try again.';

        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail) && detail.length > 0) {
            // Extract message from the API error object
            errorMsg = detail[0].msg || errorMsg;
          } else if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (typeof detail === 'object') {
            errorMsg = JSON.stringify(detail);
          }
          toast.error(errorMsg);
        }

        setErrorMessage(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setSubject('');
    setSubjectId('');
    setLevel('');
    setTags([]);
    setInputTag('');
    setDays({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    });

    setTimeSlots({
      monday: [{ from: '', to: '' }],
      tuesday: [{ from: '', to: '' }],
      wednesday: [{ from: '', to: '' }],
      thursday: [{ from: '', to: '' }],
      friday: [{ from: '', to: '' }],
      saturday: [{ from: '', to: '' }],
      sunday: [{ from: '', to: '' }],
    });

    setDescription('');
    setErrorMessage('');
  };

  // Add a function to render the time picker as a portal
  const renderTimePickerPortal = () => {
    if (!activeTimePicker) return null;

    const { day, index, field } = activeTimePicker;
    const timeValue = stringToTime(timeSlots[day][index][field]);

    // Get the reference key for this time picker
    const refKey = `${day}-${index}-${field}`;
    const inputElement = timePickerRefs.current[refKey];

    return ReactDOM.createPortal(
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={newValue => handleTimeChange(day, index, field, newValue)}
          onClose={handleTimePickerClose}
          className="time-picker-portal"
          ampm={true}
          format="hh:mm A"
          minutesStep={15}
          open={true}
          viewRenderers={{
            hours: customTimeViewRenderer,
            minutes: customTimeViewRenderer,
          }}
          views={['hours', 'minutes']}
          view={clockView}
          onViewChange={newView => setClockView(newView)}
          slots={{
            textField: () => null, // Hide the default text field
          }}
          slotProps={{
            popper: {
              style: { zIndex: 2500 },
              placement: 'bottom-start',
              anchorEl: inputElement,
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5],
                  },
                },
              ],
            },
          }}
        />
      </LocalizationProvider>,
      document.body
    );
  };

  const renderDayTimeSlots = day => {
    if (!days[day]) return null;

    return timeSlots[day].map((slot, index) => (
      <Box key={`${day}-${index}`} className="time-slot-container">
        <Box className="time-inputs">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box className="time-input-group-group">
              <Typography className="time-label">From</Typography>
              <div
                className="time-input-wrapper"
                onClick={() => handleTimePickerOpen(day, index, 'from')}
                ref={el => {
                  // Store the ref for this specific time input field
                  timePickerRefs.current[`${day}-${index}-from`] = el;
                }}
              >
                <div className="time-input-icon">
                  <AccessTimeIcon fontSize="small" />
                </div>
                <input
                  className="time-input-field"
                  value={slot.from || ''}
                  placeholder="Select time"
                  readOnly
                />
              </div>
            </Box>

            <Box className="time-input-group-group">
              <Typography className="time-label">To</Typography>
              <div
                className="time-input-wrapper"
                onClick={() => handleTimePickerOpen(day, index, 'to')}
                ref={el => {
                  // Store the ref for this specific time input field
                  timePickerRefs.current[`${day}-${index}-to`] = el;
                }}
              >
                <div className="time-input-icon">
                  <AccessTimeIcon fontSize="small" />
                </div>
                <input
                  className="time-input-field"
                  value={slot.to || ''}
                  placeholder="Select time"
                  readOnly
                />
              </div>
            </Box>
          </LocalizationProvider>

          <Box className="time-slot-actions">
            <IconButton className="time-slot-action-button" onClick={() => addTimeSlot(day)}>
              <AddIcon fontSize="small" />
            </IconButton>

            <IconButton
              className="time-slot-action-button"
              onClick={e => openCopyPopover(e, day, index)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>

            <IconButton
              className="time-slot-action-button"
              onClick={() => removeTimeSlot(day, index)}
              disabled={timeSlots[day].length === 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {errorMessage && errorMessage.includes(`${day.charAt(0).toUpperCase() + day.slice(1)}`) && (
          <Typography
            className="error-message"
            sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    ));
  };

  // Add this useEffect to help with debugging
  useEffect(() => {
    console.log('showSuccess state changed:', showSuccess);
  }, [showSuccess]);

  // Check if all required fields are filled
  const isFormValid = () => {
    // Check if subject is selected
    if (!subject) return false;

    // Check if level is selected
    if (!level) return false;

    // Check if at least one day is selected and has valid time slots
    const hasValidTimeSlots = Object.keys(days).some(day => {
      if (!days[day]) return false;
      return timeSlots[day].some(slot => slot.from && slot.to);
    });

    return hasValidTimeSlots;
  };

  // Create a clean reset function
  const handleClose = () => {
    setShowSuccess(false);
    resetForm();
    onClose();
  };

  // Helper function to convert UTC time to local time
  const convertUTCToLocal = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format
    const [hours, minutes] = timeString.split(':');
    const utcDate = dayjs.utc().hour(hours).minute(minutes);
    return utcDate.local().format('HH:mm');
  };

  // Helper function to convert local time to UTC
  const convertLocalToUTC = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format or '12-hour format with AM/PM'
    let localDate;
    if (timeString.includes('AM') || timeString.includes('PM')) {
      localDate = dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
    } else {
      const [hours, minutes] = timeString.split(':');
      localDate = dayjs().hour(hours).minute(minutes);
    }
    return localDate.utc().format('HH:mm');
  };

  return (
    <Modal open={open} onClose={handleClose} className="group-session-modal">
      <Box className="group-session-modal-content">
        {showSuccess ? (
          <ScheduleCreationSuccessModel sessionType="Group" />
        ) : (
          <>
            <Box className="group-session-modal-header">
              <Typography className="group-session-modal-title">
                {subject ? 'Add a' : 'Create a'} New Group Session
              </Typography>
              <IconButton onClick={handleClose} className="close-button">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box className="group-session-modal-body">
              {/* {errorMessage && !errorMessage.includes('overlaps') && (
                <Alert severity="error" className="form-error">
                  {errorMessage}
                </Alert>
              )} */}

              <Box className="select-container">
                <Typography className="select-label">Subject</Typography>
                <FormControl fullWidth variant="outlined" className="select-field">
                  <Select
                    value={subject}
                    onChange={handleSubjectChange}
                    displayEmpty
                    renderValue={value => value || 'Select Subject'}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          zIndex: 9999,
                        },
                      },
                    }}
                    IconComponent={props => (
                      <img
                        src={DropDownIcon || '/placeholder.svg'}
                        alt="dropdown"
                        className="dropdown-icon"
                        style={{ marginRight: '14px' }}
                      />
                    )}
                    sx={{
                      '& .MuiSelect-select': {
                        cursor: 'pointer',
                      },
                      '& .MuiMenuItem-root': {
                        cursor: 'pointer',
                      },
                    }}
                  >
                    {Array.isArray(subjects) &&
                      subjects.length > 0 &&
                      subjects.map(subjectItem => (
                        <MenuItem
                          key={subjectItem.id}
                          value={subjectItem.title}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 102, 255, 0.05)',
                            },
                          }}
                        >
                          {subjectItem.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>

              <Box className="select-container">
                <Typography className="select-label">Tags</Typography>
                <Autocomplete
                  multiple
                  id="tags-filled"
                  options={availableTags || []}
                  getOptionLabel={option => {
                    // Handle both string tags and object tags
                    if (typeof option === 'string') return option;
                    return option?.title || '';
                  }}
                  value={tags || []}
                  onChange={handleTagChange}
                  inputValue={inputTag}
                  onInputChange={handleTagInputChange}
                  renderTags={() => null}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={subject ? 'Type here...' : 'Select a subject first'}
                      className="tags-input"
                      // helperText={!subject ? 'Please select a subject to enable tags' : ''}
                    />
                  )}
                  renderOption={(props, option) => {
                    const label = typeof option === 'string' ? option : option?.title || '';
                    return <li {...props}>{label}</li>;
                  }}
                  disableCloseOnSelect
                  disabled={!subject}
                  className="tags-autocomplete"
                  isOptionEqualToValue={(option, value) => {
                    if (typeof option === 'string' && typeof value === 'string') {
                      return option === value;
                    }
                    return option?.id === value?.id || option?.title === value?.title;
                  }}
                  freeSolo={false}
                  filterOptions={(options, state) => {
                    // Custom filter to improve matching
                    const { inputValue } = state;
                    return options.filter(option => {
                      const label = typeof option === 'string' ? option : option?.title || '';
                      return label.toLowerCase().includes(inputValue.toLowerCase());
                    });
                  }}
                />
                {tags && tags.length > 0 && (
                  <Box className="tags-container">
                    {tags.map((tag, index) => (
                      <Chip
                        key={tag.id || `tag-${index}`}
                        label={typeof tag === 'string' ? tag : tag?.title || ''}
                        onDelete={() => handleTagDelete(tag)}
                        className="tag-chip"
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Box className="select-container">
                <Typography className="select-label">Level to Teach</Typography>
                <FormControl fullWidth variant="outlined" className="select-field">
                  <Select
                    value={level}
                    onChange={e => {
                      console.log('Level selected:', e.target.value);
                      setLevel(e.target.value);
                      // Clear error when changing level
                      setErrorMessage('');
                    }}
                    displayEmpty
                    renderValue={value => value || 'Select Level'}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null, // This is important for proper positioning
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          zIndex: 9999,
                        },
                      },
                    }}
                    IconComponent={props => (
                      <img
                        src={DropDownIcon || '/placeholder.svg'}
                        alt="dropdown"
                        className="dropdown-icon"
                        style={{ marginRight: '14px' }}
                      />
                    )}
                    sx={{
                      '& .MuiSelect-select': {
                        cursor: 'pointer',
                      },
                      '& .MuiMenuItem-root': {
                        cursor: 'pointer',
                      },
                    }}
                  >
                    {teacherData &&
                      teacherData.level_of_teaching &&
                      teacherData.level_of_teaching.map(apiLevel => {
                        // Get the display value from the reverse map
                        const displayLevel = ReverseLevelMap[apiLevel];
                        if (displayLevel) {
                          return (
                            <MenuItem
                              key={apiLevel}
                              value={displayLevel}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 102, 255, 0.05)',
                                },
                              }}
                            >
                              {displayLevel}
                            </MenuItem>
                          );
                        }
                        return null;
                      })}
                  </Select>
                </FormControl>
              </Box>

              <Box className="days-selection">
                {Object.keys(days).map(day => (
                  <Box key={day} className="day-container">
                    <Box className="day-header">
                      <Typography className="day-name-group">
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </Typography>
                      <Switch
                        checked={days[day]}
                        onChange={() => handleDayToggle(day)}
                        color="primary"
                        className="day-switch-group"
                      />
                    </Box>
                    {renderDayTimeSlots(day)}
                  </Box>
                ))}
              </Box>

              <Box className="description-container">
                <Typography className="description-label">Write a short description</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter here..."
                  value={description}
                  onChange={e => {
                    setDescription(e.target.value);
                    setErrorMessage('');
                  }}
                  variant="outlined"
                  className="description-field"
                />
              </Box>
            </Box>

            <Box className="group-session-modal-footer">
              <Button
                variant="outlined"
                onClick={handleClose}
                className="cancel-button-group-session"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                className="create-confirm-button"
                disabled={isLoading || !isFormValid()}
              >
                {isLoading ? 'Processing...' : 'Create'}
              </Button>
            </Box>

            <Popover
              open={Boolean(copyAnchorEl)}
              anchorEl={copyAnchorEl}
              onClose={closeCopyPopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              className="copy-popover"
            >
              <Box className="copy-popover-content">
                <Typography className="copy-popover-title">Copy Times TO</Typography>
                <Box className="copy-days-list">
                  {Object.keys(copyTargetDays)
                    .filter(day => day !== copySourceDay)
                    .map(day => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={copyTargetDays[day]}
                            onChange={() => handleCopyTargetChange(day)}
                            color="primary"
                          />
                        }
                        label={day.charAt(0).toUpperCase() + day.slice(1)}
                        className="copy-day-checkbox"
                      />
                    ))}
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={applyCopy}
                  className="apply-copy-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>

            {/* Add the time picker portal at the end */}
            {renderTimePickerPortal()}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default CreateGroupSessionModel;
